const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';

const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

/**
 * Obtiene las preasignaciones de un estudiante
 * @param {number} estudianteId - ID del estudiante
 * @param {number} cicloId - ID del ciclo escolar (opcional)
 * @returns {Promise<Array>} - Lista de preasignaciones
 */
export async function getPreasignacionesPorEstudiante(estudianteId, cicloId = null) {
  if (!estudianteId) return [];
  
  try {
    let url = `${API_BASE}/escolar/preasignaciones/por-estudiante/${estudianteId}/`;
    if (cicloId) {
      url += `?id_ciclo_escolar=${cicloId}`;
    }
    
    const res = await fetch(url, {
      headers: getAuthHeaders()
    });
    
    if (!res.ok) {
      console.error('[preasignaciones.js] Error obteniendo preasignaciones:', res.status);
      return [];
    }
    
    return res.json();
  } catch (error) {
    console.error('[preasignaciones.js] Error obteniendo preasignaciones:', error);
    return [];
  }
}

/**
 * Obtiene las materias sugeridas para la preasignación de un estudiante,
 * ya clasificadas por el backend en: recursadas, siguientes y opcionales.
 * @param {number} estudianteId - ID del estudiante
 * @returns {Promise<{meta:Object, recursadas:Array, siguientes:Array, opcionales:Array}>}
 */
export async function getMateriasSugeridas(estudianteId) {
  const vacio = { meta: {}, recursadas: [], siguientes: [], opcionales: [] };
  if (!estudianteId) return vacio;

  try {
    const res = await fetch(
      `${API_BASE}/escolar/preasignaciones/materias-sugeridas/${estudianteId}/`,
      { headers: getAuthHeaders() }
    );

    if (!res.ok) {
      console.error('[preasignaciones.js] Error obteniendo materias sugeridas:', res.status);
      return vacio;
    }

    const data = await res.json();
    return {
      meta: data.meta || {},
      recursadas: data.recursadas || [],
      siguientes: data.siguientes || [],
      opcionales: data.opcionales || [],
    };
  } catch (error) {
    console.error('[preasignaciones.js] Error obteniendo materias sugeridas:', error);
    return vacio;
  }
}

/**
 * Guarda múltiples preasignaciones a la vez
 * @param {number|null} idInscripcion - ID de la inscripción del estudiante (opcional)
 * @param {Array<number>} materias - Array de IDs de materias a preasignar
 * @param {string} tipo - Tipo de preasignación (AUTOMATICA o MANUAL)
 * @param {number|null} idEstudiante - ID del estudiante (usado si no hay inscripción)
 * @param {Object} [options] - Opciones adicionales
 * @param {boolean} [options.reemplazar] - Si es true, elimina las preasignaciones previas antes de guardar
 * @param {Array<number>} [options.materiasManual] - Subconjunto de materias que se marcan como MANUAL
 * @returns {Promise<Object>} - Resultado del guardado
 */
export async function guardarPreasignaciones(idInscripcion, materias, tipo = 'MANUAL', idEstudiante = null, options = {}) {
  if (!materias || materias.length === 0) {
    throw new Error('Se requieren materias para preasignar');
  }
  
  const payload = {
    materias: materias,
    tipo_preasignacion: tipo
  };
  
  // Incluir id_inscripcion si tiene valor
  if (idInscripcion) {
    payload.id_inscripcion = idInscripcion;
  }
  
  // Incluir id_estudiante si tiene valor (usado cuando no hay inscripción)
  if (idEstudiante) {
    payload.id_estudiante = idEstudiante;
  }

  // Reemplazar limpia las preasignaciones previas del estudiante en el backend
  if (options.reemplazar) {
    payload.reemplazar = true;
  }

  // Materias marcadas manualmente por el usuario (opcionales)
  if (options.materiasManual && options.materiasManual.length > 0) {
    payload.materias_manual = options.materiasManual;
  }

  // Confirmación explícita del usuario cuando se supera el máximo de 6 materias
  if (options.confirmarExceso) {
    payload.confirmar_exceso = true;
  }
  
  const res = await fetch(`${API_BASE}/escolar/preasignaciones/guardar-multiple/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    body: JSON.stringify(payload)
  });
  
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Error guardando preasignaciones: ${res.status} - ${text}`);
  }
  
  return res.json();
}

/**
 * Elimina múltiples preasignaciones
 * @param {number} idInscripcion - ID de la inscripción
 * @param {Array<number>} materias - Array de IDs de materias
 * @returns {Promise<Object>} - Resultado de la eliminación
 */
export async function eliminarPreasignaciones(idInscripcion, materias) {
  const payload = {
    id_inscripcion: idInscripcion,
    materias: materias
  };
  
  const res = await fetch(`${API_BASE}/escolar/preasignaciones/eliminar-multiple/`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    body: JSON.stringify(payload)
  });
  
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Error eliminando preasignaciones: ${res.status} - ${text}`);
  }
  
  return res.json();
}

/**
 * Anula TODAS las preasignaciones de un estudiante.
 * También hace soft-delete de los grupos asignados (si no tienen calificaciones).
 * @param {number} idEstudiante - ID del estudiante
 * @returns {Promise<Object>} - Resultado de la anulación con advertencias si hay cardex
 */
export async function anularPreasignacion(idEstudiante) {
  const res = await fetch(`${API_BASE}/escolar/preasignaciones/anular/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    body: JSON.stringify({ id_estudiante: idEstudiante })
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Error anulando preasignación: ${res.status} - ${text}`);
  }

  return res.json();
}

/**
 * Elimina registros de EscCardex para un estudiante y materia específicos.
 * Usar para corregir materias incorrectas en el historial académico.
 * @param {number} idEstudiante
 * @param {number} idMateria
 * @returns {Promise<Object>}
 */
export async function eliminarDeCardex(idEstudiante, idMateria) {
  const res = await fetch(`${API_BASE}/escolar/preasignaciones/eliminar-de-cardex/`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders()
    },
    body: JSON.stringify({ id_estudiante: idEstudiante, id_materia: idMateria })
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(`Error eliminando del kardex: ${res.status} - ${text}`);
  }

  return res.json();
}

/**
 * Obtiene todas las preasignaciones (con filtros opcionales)
 * @param {Object} filtros - Filtros opcionales
 * @returns {Promise<Array>} - Lista de preasignaciones
 */
export async function getPreasignaciones(filtros = {}) {
  const params = new URLSearchParams();
  if (filtros.id_inscripcion) params.append('id_inscripcion', filtros.id_inscripcion);
  if (filtros.id_estudiante) params.append('id_estudiante', filtros.id_estudiante);
  if (filtros.id_materia) params.append('id_materia', filtros.id_materia);
  if (filtros.id_ciclo_escolar) params.append('id_ciclo_escolar', filtros.id_ciclo_escolar);
  
  const url = `${API_BASE}/escolar/preasignaciones/${params.toString() ? '?' + params.toString() : ''}`;
  
  const res = await fetch(url, {
    headers: getAuthHeaders()
  });
  
  if (!res.ok) {
    console.error('[preasignaciones.js] Error obteniendo preasignaciones:', res.status);
    return [];
  }
  
  const data = await res.json();
  return Array.isArray(data) ? data : (data.results || []);
}

export default {
  getPreasignacionesPorEstudiante,
  getMateriasSugeridas,
  guardarPreasignaciones,
  eliminarPreasignaciones,
  anularPreasignacion,
  eliminarDeCardex,
  getPreasignaciones
};
